/**
 * Delivery Cockpit guided tour step graphs per persona.
 *
 * @typedef {object} TourStep
 * @property {string} id
 * @property {string} title
 * @property {string} body
 * @property {string} [route] - Expected pathname + optional search for completion.
 * @property {{ label: string, path: string }} [cta]
 * @property {string} [tourTarget] - data-tour selector hint for phase 2 spotlight.
 * @property {'route'|'manual'|'optional'} [completion] - How to unlock Next.
 * @property {string} [roleNote]
 */

import { getTourIdForPersona } from './deliveryCockpitTourPersonas';

/**
 * Build welcome step with optional session hint.
 *
 * @param {string} [firstName]
 * @param {string} [hint]
 * @returns {TourStep}
 */
const welcomeStep = (firstName, hint) => ({
    id: 'welcome',
    title: firstName ? `Welcome, ${firstName}` : 'Welcome to synaptix Pulse',
    body: hint
        ? `This is your Delivery Cockpit — one place to run engineering projects from staffing and time entry through billing. ${hint}`
        : 'This is your Delivery Cockpit — one place to run engineering projects from staffing and time entry through billing. This guided tour walks your role through each step.',
    completion: 'manual',
});

const doneStep = (personaLabel) => ({
    id: 'done',
    title: 'Golden path complete',
    body: `You have walked the ${personaLabel} workflow. Re-open this guide anytime from Help → Full tour or Resume tour.`,
    completion: 'manual',
});

/** @type {TourStep[]} */
export const PM_GOLDEN_PATH_STEPS = [
    welcomeStep(),
    {
        id: 'landing',
        title: 'Your delivery home',
        body: 'You land on Projects. The Today strip at the top is your ranked inbox — Exceptions, Approvals, Staffing, and WIP / AR chips with deep links. Start there, then use the header Billing queue shortcut when finance needs WIP cleared.',
        route: '/project-operations',
        cta: { label: 'Open Projects', path: '/project-operations' },
        tourTarget: 'tour-delivery-header',
        completion: 'route',
    },
    {
        id: 'nav',
        title: 'Sidebar map',
        body: 'The Delivery Cockpit keeps navigation focused — Projects, Timesheets, Expenses, and Billing & Invoices. Fewer tabs means faster decisions.',
        tourTarget: 'tour-main-nav',
        completion: 'optional',
    },
    {
        id: 'portfolio',
        title: 'Portfolio exceptions',
        body: 'The Portfolio tab holds the full exception list for the selected reporting period, plus live jobs with contract value. Today shows the top chips; Portfolio is where you scan every project that needs attention.',
        route: '/project-operations',
        cta: { label: 'Portfolio tab', path: '/project-operations?tab=portfolio' },
        tourTarget: 'tour-portfolio-tab',
        completion: 'route',
    },
    {
        id: 'portfolio-row',
        title: 'Pick a project',
        body: 'Identify a live project row — use Control to open the control center for that job. Exception chips name the alert type in plain language (for example Margin at risk or WIP billing).',
        tourTarget: 'tour-portfolio-table',
        completion: 'optional',
    },
    {
        id: 'control-open',
        title: 'Open control center',
        body: 'Click Control on a portfolio row. Portfolio, Control, and Staffing share one reporting period — the same dates follow you when you drill in.',
        route: '/project-operations',
        cta: { label: 'Control center tab', path: '/project-operations?tab=control' },
        tourTarget: 'tour-project-control',
        completion: 'route',
    },
    {
        id: 'control-hero',
        title: 'Read hero metrics',
        body: 'Hero metrics summarize margin at risk, hours approved vs planned, net labor multiplier, and budget consumed — all from live project data. Earned value lives under Performance & evidence, not the hero row.',
        tourTarget: 'tour-control-hero',
        completion: 'optional',
    },
    {
        id: 'phase-burn',
        title: 'Phase fee burn',
        body: 'Phase burn captions show which design phase is consuming fee fastest — e.g. Schematic Design at 72% of fee consumed.',
        tourTarget: 'tour-phase-burn',
        completion: 'optional',
    },
    {
        id: 'control-alerts',
        title: 'Action required',
        body: 'Action required opens by default when there is work to clear — margin, staffing, billing, phase, budget, or change orders. Each row shows a human label, owner, and next action. Diagnostics (Plan vs approved actuals, EVM-lite, phase variance) sit under Performance & evidence below.',
        tourTarget: 'tour-control-alerts',
        completion: 'optional',
    },
    {
        id: 'staffing-tab',
        title: 'Staffing on project',
        body: 'The Staffing tab shows who is on the project week by week and flags cross-project overload. It uses the same reporting period as Control — not a separate ERP module.',
        route: '/project-operations',
        cta: { label: 'Staffing tab', path: '/project-operations?tab=staffing' },
        tourTarget: 'tour-staffing-tab',
        completion: 'route',
    },
    {
        id: 'staffing-grid',
        title: 'Week grid',
        body: 'Use the project picker if prompted, then review the allocation grid and overload callouts. Scope chips label approved actuals vs future capacity so you know which numbers are lagging vs forecast.',
        tourTarget: 'tour-staffing-picker',
        completion: 'optional',
    },
    {
        id: 'billing-shortcut',
        title: 'Billing queue shortcut',
        body: 'Jump to uninvoiced WIP in one click from the Projects header — the fastest path from delivery to finance.',
        route: '/invoice-management',
        cta: { label: 'Billing queue', path: '/invoice-management?tab=billing-queue' },
        tourTarget: 'tour-billing-queue-link',
        completion: 'route',
    },
    {
        id: 'timesheets-nav',
        title: 'Timesheets + badge',
        body: 'Engineers log time by project and phase. Open Timesheets to approve submitted weeks — watch the nav badge for pending approvals. Approvals unlock WIP; the Today Approvals chip jumps here too.',
        route: '/timesheets',
        cta: { label: 'Timesheets', path: '/timesheets' },
        tourTarget: 'tour-nav-timesheets',
        completion: 'route',
    },
    {
        id: 'pending-filter',
        title: 'Pending approvals',
        body: 'Use the Pending my approval chip to filter timesheets waiting for your action. Approved hours feed WIP and margin.',
        route: '/timesheets',
        tourTarget: 'tour-pending-approval-chip',
        completion: 'optional',
    },
    {
        id: 'approve',
        title: 'Approve a week',
        body: 'Open a submitted timesheet and approve it. This unlocks finance pre-bill review and the billing queue.',
        completion: 'optional',
    },
    {
        id: 'billing-home',
        title: 'Billing & Invoices',
        body: 'Finance reviews invoices and AR from this home. You can return here from the sidebar anytime.',
        route: '/invoice-management',
        cta: { label: 'Billing & Invoices', path: '/invoice-management' },
        tourTarget: 'tour-invoice-header',
        completion: 'route',
    },
    {
        id: 'billing-queue',
        title: 'WIP queue',
        body: 'The Billing queue tab surfaces uninvoiced WIP and overdue AR with Amount and Age columns, plus Open WIP / AR aging scope labels from live project data.',
        route: '/invoice-management',
        cta: { label: 'Billing queue tab', path: '/invoice-management?tab=billing-queue' },
        tourTarget: 'tour-billing-queue-tab',
        completion: 'route',
    },
    doneStep('project manager'),
];

/** @type {TourStep[]} */
export const IC_TIMESHEET_PATH_STEPS = [
    welcomeStep(),
    {
        id: 'workspace',
        title: 'My Workspace home',
        body: 'Your home screen lists assigned work and shortcuts. Open timesheets from here to log hours against the projects and phases your PM expects.',
        route: '/my-workspace',
        cta: { label: 'My Workspace', path: '/my-workspace' },
        tourTarget: 'tour-workspace-header',
        completion: 'route',
    },
    {
        id: 'open-timesheets',
        title: 'Open timesheets',
        body: 'Use the Open timesheets link to jump straight to your weekly timesheet list.',
        route: '/timesheets',
        cta: { label: 'Open timesheets', path: '/timesheets' },
        tourTarget: 'tour-open-timesheets',
        completion: 'route',
    },
    {
        id: 'timesheets-page',
        title: 'Timesheets list',
        body: 'Table-first timesheets show draft, submitted, and approved weeks. Filter by status or week start.',
        route: '/timesheets',
        completion: 'route',
    },
    {
        id: 'new-timesheet',
        title: 'Create your week',
        body: 'Click New Timesheet to open the current week, or Open an existing draft.',
        route: '/timesheets',
        tourTarget: 'tour-new-timesheet',
        completion: 'optional',
    },
    {
        id: 'add-line',
        title: 'Add project / phase line',
        body: 'Add time entries by project and phase — the same structure your PM uses for margin and billing.',
        completion: 'optional',
    },
    {
        id: 'phase-importance',
        title: 'Why phase matters',
        body: 'Phase-level time keeps earned value and fee burn accurate. Always pick the phase your PM assigned.',
        completion: 'manual',
    },
    {
        id: 'hours',
        title: 'Enter hours',
        body: 'Enter daily or weekly hours on each line. Save as you go — drafts persist until you submit.',
        completion: 'optional',
    },
    {
        id: 'save-draft',
        title: 'Save draft',
        body: 'Draft timesheets stay editable until you submit. You can return from My Workspace anytime.',
        completion: 'optional',
    },
    {
        id: 'submit',
        title: 'Submit for approval',
        body: 'Submit sends the week to your manager. You will see a Submitted status chip when it is in the queue.',
        completion: 'optional',
    },
    {
        id: 'status',
        title: 'Submitted status',
        body: 'After submit, your manager approves in Timesheets, then finance can include hours in pre-bill review.',
        completion: 'manual',
    },
    doneStep('engineer'),
];

/** @type {TourStep[]} */
export const APPROVER_QUEUE_PATH_STEPS = [
    welcomeStep(),
    {
        id: 'timesheets-landing',
        title: 'Timesheets home',
        body: 'As a team lead you approve submitted weeks from the same Timesheets list engineers use.',
        route: '/timesheets',
        cta: { label: 'Timesheets', path: '/timesheets' },
        completion: 'route',
    },
    {
        id: 'pending-badge',
        title: 'Pending badge',
        body: 'The nav badge shows how many timesheets await your approval — click Timesheets to action them.',
        route: '/timesheets',
        tourTarget: 'tour-nav-timesheets',
        completion: 'route',
    },
    {
        id: 'pending-filter',
        title: 'Pending My Approval',
        body: 'Use the Pending my approval chip to filter to timesheets you can action right now.',
        tourTarget: 'tour-pending-approval-chip',
        completion: 'optional',
    },
    {
        id: 'open-row',
        title: 'Open a submitted week',
        body: 'Open a submitted timesheet to review lines, hours, and project allocation.',
        completion: 'optional',
    },
    {
        id: 'review-lines',
        title: 'Review lines',
        body: 'Check hours by project and phase. Flag concerns if allocation looks wrong — that escalates for senior review.',
        completion: 'optional',
    },
    {
        id: 'approve',
        title: 'Approve',
        body: 'Approve when hours look correct. Approved time flows to finance and the billing queue.',
        tourTarget: 'tour-timesheet-approve',
        completion: 'optional',
    },
    {
        id: 'reject-path',
        title: 'Reject path (optional)',
        body: 'Reject with a comment when hours need correction — the engineer can resubmit after fixing.',
        completion: 'optional',
    },
    {
        id: 'pm-concern',
        title: 'PM concerns (FYI)',
        body: 'Project managers can flag concerns without blocking approval — useful for staffing feedback.',
        completion: 'manual',
    },
    doneStep('approver'),
];

/** @type {TourStep[]} */
export const FINANCE_BILLING_PATH_STEPS = [
    welcomeStep(),
    {
        id: 'billing-home',
        title: 'Billing & Invoices',
        body: 'This is your finance home for client billing. The Invoices tab lists drafts and sent invoices.',
        route: '/invoice-management',
        cta: { label: 'Billing & Invoices', path: '/invoice-management' },
        tourTarget: 'tour-invoice-header',
        completion: 'route',
    },
    {
        id: 'invoices-tab',
        title: 'Invoices overview',
        body: 'Filter by status — Draft, Sent, Paid — and search by invoice number, project, or client.',
        route: '/invoice-management',
        completion: 'route',
    },
    {
        id: 'billing-queue-tab',
        title: 'Billing queue tab',
        body: 'The Billing queue tab surfaces uninvoiced WIP and overdue AR from live project data.',
        route: '/invoice-management',
        cta: { label: 'Billing queue', path: '/invoice-management?tab=billing-queue' },
        tourTarget: 'tour-billing-queue-tab',
        completion: 'route',
    },
    {
        id: 'wip-panel',
        title: 'WIP panel',
        body: 'Queue rows show Amount and Age for uninvoiced WIP and overdue AR — create invoices or record collections. Scope chips say Open WIP as of / AR aging as of so period meaning is clear.',
        completion: 'optional',
    },
    {
        id: 'queue-action',
        title: 'Queue row action',
        body: 'Open a queue row to draft an invoice from approved time and expenses, or write off stale WIP that will not be billed.',
        completion: 'optional',
    },
    {
        id: 'create-invoice',
        title: 'Create invoice',
        body: 'Use Create invoice for ad-hoc billing or pre-bill packages before month-end close.',
        completion: 'optional',
    },
    {
        id: 'invoice-detail',
        title: 'Invoice detail',
        body: 'Review line items, status workflow, and pre-bill review thread before sending to the client.',
        completion: 'optional',
    },
    {
        id: 'csv-export',
        title: 'CSV export',
        body: 'Export the invoice register or individual invoices to CSV for your GL — no manual re-keying.',
        completion: 'optional',
    },
    {
        id: 'pre-bill',
        title: 'Pre-bill vs approved time',
        body: 'Workflow: manager-approved timesheets → pre-bill review → invoice → CSV export.',
        completion: 'manual',
    },
    {
        id: 'ar-overdue',
        title: 'AR / overdue',
        body: 'Overdue AR appears in the billing queue with Collect payment links back to project control.',
        completion: 'optional',
    },
    {
        id: 'expenses-mention',
        title: 'Expenses (secondary)',
        body: 'Expense Management in the sidebar handles reimbursable costs — billable expenses can flow into invoices.',
        route: '/expenses',
        cta: { label: 'Expenses', path: '/expenses' },
        completion: 'optional',
    },
    {
        id: 'deferred-note',
        title: 'Edition note',
        body: 'Full GL and enterprise analytics walls are not in this Delivery Cockpit edition — billing queue and invoices are your month-end hub.',
        completion: 'manual',
    },
    doneStep('finance'),
];

/**
 * Pick PM golden-path steps by id (stable when the PM graph gains/loses optional steps).
 *
 * @param {string[]} stepIds
 * @returns {TourStep[]}
 */
const pmStepsById = (stepIds) => {
    const byId = Object.fromEntries(PM_GOLDEN_PATH_STEPS.map((step) => [step.id, step]));
    return stepIds.map((id) => byId[id]).filter(Boolean);
};

/** Executive: portfolio → control → staffing → billing focus. */
export const EXEC_DELIVERY_PATH_STEPS = [
    ...pmStepsById([
        'welcome',
        'landing',
        'nav',
        'portfolio',
        'portfolio-row',
        'control-open',
        'control-hero',
        'phase-burn',
        'control-alerts',
        'staffing-tab',
        'billing-shortcut',
        'billing-home',
        'billing-queue',
    ]),
    doneStep('executive'),
];

/** Admin: PM path + finance billing steps + note. */
export const ADMIN_FULL_PATH_STEPS = [
    ...PM_GOLDEN_PATH_STEPS.filter((step) => step.id !== 'done'),
    ...FINANCE_BILLING_PATH_STEPS.filter((s) => ['billing-queue-tab', 'csv-export', 'deferred-note'].includes(s.id)),
    {
        id: 'admin-deferred',
        title: 'Deferred routes',
        body: 'On the public production edition, enterprise modules (CRM, analytics walls) return Page Not Found — use Delivery Cockpit routes for demos.',
        completion: 'manual',
        roleNote: 'admin',
    },
    doneStep('admin'),
];

/** Minimal client portal tour. */
export const CLIENT_PORTAL_PATH_STEPS = [
    welcomeStep(),
    {
        id: 'client-portal',
        title: 'Client portal',
        body: 'View project status and documents shared with your organization. Delivery Cockpit PM tools are not shown to client users.',
        completion: 'manual',
    },
    doneStep('client'),
];

/** @type {Record<string, TourStep[]>} */
export const TOUR_GRAPHS_BY_ID = {
    'pm-golden-path': PM_GOLDEN_PATH_STEPS,
    'exec-delivery-path': EXEC_DELIVERY_PATH_STEPS,
    'admin-full-path': ADMIN_FULL_PATH_STEPS,
    'ic-timesheet-path': IC_TIMESHEET_PATH_STEPS,
    'approver-queue-path': APPROVER_QUEUE_PATH_STEPS,
    'finance-billing-path': FINANCE_BILLING_PATH_STEPS,
    'client-portal-path': CLIENT_PORTAL_PATH_STEPS,
};

/**
 * Steps for a tour persona.
 *
 * @param {import('./deliveryCockpitTourPersonas').TourPersona} persona
 * @param {{ firstName?: string, pendingApprovalCount?: number }} [sessionHints]
 * @returns {TourStep[]}
 */
export const getTourStepsForPersona = (persona, sessionHints = {}) => {
    const tourId = getTourIdForPersona(persona);
    const steps = TOUR_GRAPHS_BY_ID[tourId] || PM_GOLDEN_PATH_STEPS;

    if (persona === 'pm' || persona === 'approver') {
        const count = sessionHints.pendingApprovalCount;
        if (typeof count === 'number' && count > 0 && steps[0]?.id === 'welcome') {
            return [
                welcomeStep(
                    sessionHints.firstName,
                    `You have ${count} timesheet${count === 1 ? '' : 's'} pending your approval.`,
                ),
                ...steps.slice(1),
            ];
        }
    }

    if (sessionHints.firstName && steps[0]?.id === 'welcome') {
        return [
            welcomeStep(sessionHints.firstName),
            ...steps.slice(1),
        ];
    }

    return steps;
};

/**
 * Validate tour graph integrity (unique ids per graph).
 *
 * @param {TourStep[]} steps
 * @returns {boolean}
 */
export const validateTourGraph = (steps) => {
    const ids = steps.map((s) => s.id);
    return ids.length === new Set(ids).size;
};

/**
 * @param {import('./deliveryCockpitTourPersonas').TourPersona} persona
 * @returns {boolean}
 */
export const validatePersonaTourGraph = (persona) => {
    const tourId = getTourIdForPersona(persona);
    return validateTourGraph(TOUR_GRAPHS_BY_ID[tourId] || []);
};
